import { bindable, customElement } from 'aurelia-framework';

export interface SearchableSelectOption {
  value: any;
  label: string;
}

export interface ISearchableSelectEditorParams {
  options: SearchableSelectOption[];
  allowNull?: boolean;
  nullLabel?: string;
  allowCustomValue?: boolean;
}

@customElement('ag-searchable-select-editor')
export class AgSearchableSelectEditor {
  @bindable()
  params: {
    value: any;
    stopEditing: (cancel?: boolean) => void;
    colDef: { cellEditorParams: ISearchableSelectEditorParams | (() => ISearchableSelectEditorParams) };
  };

  private selectedValue: any;
  searchText = '';
  filteredOptions: SearchableSelectOption[] = [];
  allOptions: SearchableSelectOption[] = [];
  highlightedIndex = 0;
  customValue = '';
  allowCustomValue = false;

  inputElement: HTMLInputElement;
  customValueInput: HTMLInputElement;
  optionsContainer: HTMLElement;

  bind() {
    this.selectedValue = this.params.value;

    // Get editor params - can be object or function
    const editorParams = typeof this.params.colDef.cellEditorParams === 'function'
      ? this.params.colDef.cellEditorParams()
      : this.params.colDef.cellEditorParams;

    // Build options list
    this.allOptions = [];
    if (editorParams?.allowNull !== false) {
      this.allOptions.push({ value: null, label: editorParams?.nullLabel || '(None)' });
    }
    this.allOptions.push(...(editorParams?.options || []));

    this.filteredOptions = [...this.allOptions];
    this.allowCustomValue = editorParams?.allowCustomValue === true;

    // Set initial highlight to current value
    const currentIndex = this.filteredOptions.findIndex(o => o.value === this.selectedValue);
    this.highlightedIndex = currentIndex >= 0 ? currentIndex : 0;
  }

  attached() {
    setTimeout(() => {
      this.inputElement?.focus();
    }, 10);
  }

  getValue() {
    return this.customValue?.trim() ? this.customValue.trim() : this.selectedValue;
  }

  isPopup() {
    return true;
  }

  getPopupPosition() {
    return 'under';
  }

  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const search = (input.value || '').toLowerCase().trim();
    if (!search) {
      this.filteredOptions = [...this.allOptions];
    } else {
      this.filteredOptions = this.allOptions.filter(o =>
        o.label?.toLowerCase().includes(search)
      );
    }
    this.highlightedIndex = 0;
  }

  selectOption(option: SearchableSelectOption) {
    this.selectedValue = option.value;
    this.customValue = '';
    this.params.stopEditing();
  }

  onCustomValueInput() {
    if (this.customValue) {
      this.selectedValue = null;
    }
  }

  handleCustomKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.params.stopEditing();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.params.stopEditing(true);
    }
    return true;
  }

  handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        this.scrollToHighlighted();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.filteredOptions[this.highlightedIndex]) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.params.stopEditing(true);
        break;
      case 'Tab':
        if (this.filteredOptions[this.highlightedIndex]) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
    }
    return true;
  }

  private scrollToHighlighted() {
    setTimeout(() => {
      const element = this.optionsContainer?.querySelector('.ag-searchable-select-option.highlighted');
      element?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  isHighlighted(index: number): boolean {
    return index === this.highlightedIndex;
  }

  isSelected(option: SearchableSelectOption): boolean {
    return option.value === this.selectedValue;
  }
}
